import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Custom (26.10.26): fieldset index of the variant option the customer changed last
 * ('0' = first option, e.g. bed color; '1' = second option, e.g. mouse color).
 * @type {string | null}
 */
let lastChangedFieldsetIndex = null;

document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.closest('variant-picker')) {
      lastChangedFieldsetIndex = input.dataset.fieldsetIndex ?? null;
    }
  },
  true
);

/**
 * Custom (27.10.26): returns the index of the image to show for the option that was just changed.
 * First option (e.g. bed color) -> data-variant-media-index,
 * further options (e.g. mouse color) -> data-accessory-media-index.
 * @param {Element} gallery - The media gallery rendered for the newly selected variant.
 * @returns {number | null}
 */
function getTargetMediaIndex(gallery) {
  const variantMediaIndex = gallery.getAttribute('data-variant-media-index');
  const accessoryMediaIndex = gallery.getAttribute('data-accessory-media-index');
  const changedAccessoryOption = lastChangedFieldsetIndex !== null && lastChangedFieldsetIndex !== '0';
  const target = changedAccessoryOption && accessoryMediaIndex ? accessoryMediaIndex : variantMediaIndex;
  if (target === null || target === '') return null;

  const index = Number(target);
  return Number.isInteger(index) ? index : null;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Custom (27.10.26): the gallery keeps the admin order. If the product stays the same, keep the current
        // gallery and only slide to the image of the option that was just changed.
        const keepGallery =
          this.dataset.fixedOrder === 'true' &&
          newMediaGallery.getAttribute('data-fixed-order') === 'true' &&
          this.dataset.productId === newMediaGallery.getAttribute('data-product-id');

        if (keepGallery) {
          const targetMediaIndex = getTargetMediaIndex(newMediaGallery);
          if (targetMediaIndex !== null) this.slideshow?.select(targetMediaIndex);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}